import { useEffect, useState } from "react";

import {
  isExtensionSetupEnd,
  useBrowserExtensionSetup,
} from "./browser-extension-setup";
import {
  getDenkerBrowserStatus,
  type DenkerBrowserStatus,
} from "./denker-browser";

/**
 * Where the Denker extension stands in the Denker browser - null until first
 * read - read on mount, whenever Denker's window takes focus - the user comes
 * back from the browser - and when an extension setup ends. A failed read
 * keeps the last one; `failed` says the latest read failed.
 */
export function useDenkerBrowserStatus(): {
  status: DenkerBrowserStatus | null;
  failed: boolean;
} {
  const [status, setStatus] = useState<DenkerBrowserStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const setupEnded = useBrowserExtensionSetup(({ progress }) =>
    progress && isExtensionSetupEnd(progress.step) ? progress.setupId : null,
  );
  useEffect(() => {
    let active = true;
    let latest = 0;
    const read = () => {
      const mine = ++latest;
      void getDenkerBrowserStatus().then(
        (next) => {
          if (!active || mine !== latest) return;
          setStatus(next);
          setFailed(false);
        },
        () => {
          if (active && mine === latest) setFailed(true);
        },
      );
    };
    read();
    window.addEventListener("focus", read);
    return () => {
      active = false;
      window.removeEventListener("focus", read);
    };
  }, [setupEnded]);
  return { status, failed };
}
